'use client';

import { useTranslation } from 'react-i18next';

import {
  getAemCallbackURL,
  getCurrentProductConfigurationURL,
  getFallbackURL,
} from '@storefront/services/SessionHelperService/sessionHelperService';
import ErrorInformation from '@storefront/shared-components/error-information/error-information';
import { HeldError } from '@storefront/shared-components/error-information/held-error';
import { RetryableError } from '@storefront/shared-components/error-information/retryable-error';
import Header from '@storefront/shared-components/header/header';

import styles from './error.p.module.scss';

type ErrorClientWrapperProps = {
  error?: RetryableError | HeldError | Error;
  resetErrorBoundary?: () => unknown;
};

export default function ErrorClientWrapper({
  error,
}: Readonly<ErrorClientWrapperProps>) {
  const i18nPath =
    error instanceof HeldError && error.i18nPath
      ? error.i18nPath
      : 'errors.SOMETHING_WENT_WRONG';

  const camelCaseErrorId = (i18nPath: string) =>
    i18nPath
      ?.replace('errors.', '')
      ?.split('_')
      ?.map(
        (item: string) =>
          item?.charAt(0)?.toUpperCase() + item?.slice(1)?.toLowerCase()
      )
      .join('');

  const { t, i18n } = useTranslation('common', { keyPrefix: i18nPath });

  return (
    <div className={styles['container']}>
      <Header />
      <ErrorInformation
        errorId={camelCaseErrorId(i18nPath)}
        code={t(`code`)}
        title={t(`title`)}
        description={t(`description`)}
        {...(i18n.exists(`${i18nPath}.description2`) && {
          description2: t(`description2`),
        })}
        buttonDetails={{
          redirectToURL: t('redirectUrl', {
            aemURL: getAemCallbackURL() ?? getFallbackURL(),
            hpStoreURL: getFallbackURL(),
          }),
          label: t('buttonLabel'),
        }}
        redirectLink={
          i18n.exists(`${i18nPath}.redirectLink`) && {
            title: t('redirectTitle'),
            link: t('redirectLink', {
              currentProductConfigurationURL:
                getCurrentProductConfigurationURL() ?? getFallbackURL(),
            }),
          }
        }
      />
    </div>
  );
}
